import { AxiosError } from 'axios';
import { create } from 'zustand';
import { toast } from '@/components';
import { EngineService } from '@/services/engine';
import {
    DateTimeMode,
    DEFAULT_CONFIDENCE_THRESHOLD,
    type QueryFilters,
} from '@/utils/query-filters';
import {
    pruneFoldersContaining,
    type SelectedFolders,
} from '@/stores/sources.store';

export interface AttachmentFile {
    url: string;
    filename: string;
}

const revokePreviewUrl = (url: string | undefined): void => {
    if (url) {
        URL.revokeObjectURL(url);
    }
};

let activeUploadGeneration = 0;
const invalidateActiveUpload = () => {
    activeUploadGeneration += 1;
};

const getAttachmentUploadErrorMessage = (error: unknown): string => {
    const status = error instanceof AxiosError && error.response?.status;
    if (status === 400) return 'Failed to upload file: invalid image format';
    if (status === 413) return 'Failed to upload file: file is too large';
    return 'Failed to upload file. Please try again.';
};

interface DraftSourceSelection {
    selected: Set<string>;
    selectedFolders: SelectedFolders;
    excluded: Set<string>;
    all: boolean;
}

export const DEFAULT_DRAFT_FILTERS: QueryFilters = {
    dateTime: { mode: DateTimeMode.LAST_HOUR },
    confidence: { enabled: true, min: DEFAULT_CONFIDENCE_THRESHOLD, max: 100 },
    sceneOnly: false,
};

const DEFAULT_DRAFT_SOURCES: DraftSourceSelection = {
    selected: new Set(),
    selectedFolders: new Map(),
    excluded: new Set(),
    all: false,
};

interface QueryPanelState {
    isMinimized: boolean;
    searchQuery: string;
    attachmentFile: AttachmentFile | undefined;
    uploadPreviewUrl: string | undefined;
    uploadingFileName: string | undefined;
    isUploading: boolean;
    uploadProgress: number;
    attachmentSnapshotHidden: boolean;
    draftSources: DraftSourceSelection;
    draftFilters: QueryFilters;
    draftObjectClassIds: Set<number>;

    setIsMinimized: (minimized: boolean) => void;
    setSearchQuery: (query: string) => void;
    setAttachmentFile: (attachment: AttachmentFile | undefined) => void;
    clearAttachmentFile: () => void;
    attachFile: (file: File) => Promise<boolean>;
    setAttachmentSnapshotHidden: (hidden: boolean) => void;
    minimize: () => void;
    expand: () => void;
    reset: () => void;

    toggleSource: (id: string) => void;
    selectSource: (id: string) => void;
    deselectSource: (id: string) => void;
    selectSources: (ids: string[]) => void;
    deselectSources: (ids: string[]) => void;
    selectFolderSources: (folderId: string, memberIds: string[]) => void;
    setSelectedSources: (ids: Set<string>, folders?: SelectedFolders) => void;
    clearSourceSelection: () => void;
    setDateTimeFilter: (
        mode: DateTimeMode,
        customRange?: { startDate: Date; endDate: Date },
    ) => void;
    clearDateTimeFilter: () => void;
    setConfidenceFilter: (enabled: boolean, min?: number, max?: number) => void;
    setDraftSourceSelection: (selected: Set<string>) => void;
    setSceneOnly: (enabled: boolean) => void;
    setObjectClassSelection: (classIds: Set<number>) => void;
    clearObjectClassSelection: () => void;
}

export const useQueryPanelStore = create<QueryPanelState>((set, get) => ({
    isMinimized: true,
    searchQuery: '',
    attachmentFile: undefined,
    uploadPreviewUrl: undefined,
    uploadingFileName: undefined,
    isUploading: false,
    uploadProgress: 0,
    attachmentSnapshotHidden: false,
    draftSources: DEFAULT_DRAFT_SOURCES,
    draftFilters: DEFAULT_DRAFT_FILTERS,
    draftObjectClassIds: new Set(),

    setIsMinimized: (minimized) => set({ isMinimized: minimized }),
    setSearchQuery: (query) => set({ searchQuery: query }),
    setAttachmentFile: (attachment) => {
        invalidateActiveUpload();
        set({ attachmentFile: attachment });
    },
    clearAttachmentFile: () => {
        invalidateActiveUpload();
        revokePreviewUrl(get().uploadPreviewUrl);
        set({
            attachmentFile: undefined,
            uploadPreviewUrl: undefined,
            uploadingFileName: undefined,
        });
    },
    setAttachmentSnapshotHidden: (hidden) =>
        set({ attachmentSnapshotHidden: hidden }),
    attachFile: async (file) => {
        if (get().isUploading) return false;
        revokePreviewUrl(get().uploadPreviewUrl);
        const previewUrl = URL.createObjectURL(file);
        const uploadGeneration = ++activeUploadGeneration;
        const isCurrentUpload = () =>
            activeUploadGeneration === uploadGeneration;
        set({
            uploadPreviewUrl: previewUrl,
            uploadingFileName: file.name,
            isUploading: true,
            uploadProgress: 0,
        });
        try {
            const response = await EngineService.uploadPromptAttachmentFile(
                file,
                (progress) => {
                    if (isCurrentUpload()) {
                        set({ uploadProgress: progress });
                    }
                },
            );
            if (!isCurrentUpload()) return false;
            set({
                attachmentFile: {
                    url: response.url,
                    filename: file.name,
                },
            });
            return true;
        } catch (error) {
            if (!isCurrentUpload()) return false;
            console.error('Failed to upload attachment:', error);
            toast.error(getAttachmentUploadErrorMessage(error));
            revokePreviewUrl(previewUrl);
            set({
                attachmentFile: undefined,
                uploadPreviewUrl: undefined,
                uploadingFileName: undefined,
            });
            return false;
        } finally {
            if (isCurrentUpload()) {
                set({ isUploading: false, uploadProgress: 0 });
            } else {
                revokePreviewUrl(previewUrl);
                set({
                    uploadPreviewUrl: undefined,
                    uploadingFileName: undefined,
                    isUploading: false,
                    uploadProgress: 0,
                });
            }
        }
    },
    minimize: () => set({ isMinimized: true }),
    expand: () => set({ isMinimized: false }),
    reset: () => {
        invalidateActiveUpload();
        revokePreviewUrl(get().uploadPreviewUrl);
        set({
            isMinimized: true,
            searchQuery: '',
            attachmentFile: undefined,
            uploadPreviewUrl: undefined,
            uploadingFileName: undefined,
            isUploading: false,
            uploadProgress: 0,
            attachmentSnapshotHidden: false,
            draftSources: {
                selected: new Set(),
                selectedFolders: new Map(),
                excluded: new Set(),
                all: false,
            },
            draftFilters: DEFAULT_DRAFT_FILTERS,
            draftObjectClassIds: new Set(),
        });
    },

    toggleSource: (id) =>
        set((state) => {
            const newSelected = new Set(state.draftSources.selected);
            const removing = newSelected.has(id);
            if (removing) {
                newSelected.delete(id);
            } else {
                newSelected.add(id);
            }
            return {
                draftSources: {
                    ...state.draftSources,
                    selected: newSelected,
                    selectedFolders: removing
                        ? pruneFoldersContaining(
                              state.draftSources.selectedFolders,
                              [id],
                          )
                        : state.draftSources.selectedFolders,
                    all: false,
                },
            };
        }),
    selectSource: (id) =>
        set((state) => {
            const newSelected = new Set(state.draftSources.selected);
            newSelected.add(id);
            return {
                draftSources: {
                    ...state.draftSources,
                    selected: newSelected,
                    all: false,
                },
            };
        }),
    deselectSource: (id) =>
        set((state) => {
            const newSelected = new Set(state.draftSources.selected);
            newSelected.delete(id);
            return {
                draftSources: {
                    ...state.draftSources,
                    selected: newSelected,
                    selectedFolders: pruneFoldersContaining(
                        state.draftSources.selectedFolders,
                        [id],
                    ),
                    all: false,
                },
            };
        }),
    selectSources: (ids) =>
        set((state) => {
            const newSelected = new Set(state.draftSources.selected);
            ids.forEach((id) => newSelected.add(id));
            return {
                draftSources: {
                    ...state.draftSources,
                    selected: newSelected,
                    all: false,
                },
            };
        }),
    deselectSources: (ids) =>
        set((state) => {
            const newSelected = new Set(state.draftSources.selected);
            ids.forEach((id) => newSelected.delete(id));
            return {
                draftSources: {
                    ...state.draftSources,
                    selected: newSelected,
                    selectedFolders: pruneFoldersContaining(
                        state.draftSources.selectedFolders,
                        ids,
                    ),
                    all: false,
                },
            };
        }),
    selectFolderSources: (folderId, memberIds) =>
        set((state) => {
            const newSelected = new Set(state.draftSources.selected);
            memberIds.forEach((id) => newSelected.add(id));
            const newFolders = new Map(state.draftSources.selectedFolders);
            newFolders.set(folderId, memberIds);
            return {
                draftSources: {
                    ...state.draftSources,
                    selected: newSelected,
                    selectedFolders: newFolders,
                    all: false,
                },
            };
        }),
    setSelectedSources: (ids, folders) =>
        set({
            draftSources: {
                selected: new Set(ids),
                selectedFolders: folders ?? new Map(),
                excluded: new Set(),
                all: false,
            },
        }),
    clearSourceSelection: () =>
        set({
            draftSources: {
                selected: new Set(),
                selectedFolders: new Map(),
                excluded: new Set(),
                all: false,
            },
        }),
    setDateTimeFilter: (mode, customRange) =>
        set((state) => ({
            draftFilters: {
                ...state.draftFilters,
                dateTime: {
                    mode,
                    customRange:
                        mode === DateTimeMode.CUSTOM ? customRange : undefined,
                },
            },
        })),
    clearDateTimeFilter: () =>
        set((state) => ({
            draftFilters: {
                ...state.draftFilters,
                dateTime: { mode: DateTimeMode.ALL, customRange: undefined },
            },
        })),
    setConfidenceFilter: (enabled, min = 0, max = 100) =>
        set((state) => ({
            draftFilters: {
                ...state.draftFilters,
                confidence: {
                    enabled,
                    min: enabled ? min : DEFAULT_CONFIDENCE_THRESHOLD,
                    max: enabled ? max : 100,
                },
            },
        })),
    setDraftSourceSelection: (selected) =>
        set((state) => ({
            draftSources: {
                ...state.draftSources,
                selected,
            },
        })),
    setSceneOnly: (enabled) =>
        set((state) => ({
            draftFilters: { ...state.draftFilters, sceneOnly: enabled },
        })),
    setObjectClassSelection: (classIds) =>
        set({ draftObjectClassIds: new Set(classIds) }),
    clearObjectClassSelection: () => set({ draftObjectClassIds: new Set() }),
}));

export type QueryPanelDraft = Pick<
    QueryPanelState,
    | 'draftSources'
    | 'draftFilters'
    | 'draftObjectClassIds'
    | 'toggleSource'
    | 'selectSource'
    | 'deselectSource'
    | 'selectFolderSources'
    | 'setSelectedSources'
    | 'clearSourceSelection'
    | 'setDateTimeFilter'
    | 'clearDateTimeFilter'
    | 'setConfidenceFilter'
    | 'setSceneOnly'
    | 'setObjectClassSelection'
    | 'clearObjectClassSelection'
>;
